/**
 * Ad-campaign promo handoff: a Yandex/VK ad lands the buyer on
 * coinsrubot.net/?promo=CODE&utm_source=… — we stash the code for the
 * browser session so the checkout opens with it already applied
 * (PromoInput initialCode) and the home page shows an «активирован» banner.
 *
 * sessionStorage (not localStorage): the code lives for the ad-click
 * session, not forever — a returning visitor next week isn't greeted with
 * a first-order code that no longer applies to them.
 */

const STASH_KEY = 'coinsrubot_promo'
const BANNER_HIDDEN_KEY = 'coinsrubot_promo_banner_hidden'

/** Validate the shape before stashing (same charset as the server). */
function normalize(raw: string): string | null {
  const code = raw.trim().toUpperCase()
  return /^[A-Z0-9_-]{3,32}$/.test(code) ? code : null
}

/** Call once on app mount: read ?promo= from the landing URL and stash it. */
export function stashPromoFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('promo') || params.get('promocode') || params.get('code')
    if (!raw) return
    const code = normalize(raw)
    if (!code) return
    sessionStorage.setItem(STASH_KEY, code)
    // The banner is relevant again for a fresh code even if it was hidden
    // earlier in the session.
    sessionStorage.removeItem(BANNER_HIDDEN_KEY)
  } catch {
    /* storage may be unavailable (private mode) — ads still work, no auto-apply */
  }
}

/** The stashed ad code, if any (re-read: cheap and always current). */
export function getStashedPromo(): string | null {
  try {
    return sessionStorage.getItem(STASH_KEY)
  } catch {
    return null
  }
}

/** Drop the stashed code (invalid for this buyer, or consumed). */
export function clearStashedPromo(): void {
  try {
    sessionStorage.removeItem(STASH_KEY)
  } catch { /* ignore */ }
}

/** Banner visibility: hidden after the user dismisses it (per session). */
export function isPromoBannerHidden(): boolean {
  try {
    return sessionStorage.getItem(BANNER_HIDDEN_KEY) === '1'
  } catch {
    return true
  }
}

export function hidePromoBanner(): void {
  try {
    sessionStorage.setItem(BANNER_HIDDEN_KEY, '1')
  } catch { /* ignore */ }
}
